import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface ViewPassPlan {
  plan_id: string;
  label: string;
  duration_minutes: number;
  price_micro_usd: number;
  price_usd: number;
  badge: string;
  description: string;
  includes: string[];
}

export interface ViewPassSession {
  session_id: string;
  plan_id: string;
  label: string;
  status: string;
  starts_at: string;
  expires_at: string;
  remaining_seconds: number;
  payment_id: string;
  wallet_address?: string | null;
}

export interface ViewPassStatus {
  schema_version: string;
  has_private_access: boolean;
  access_source: 'none' | 'role' | 'viewpass';
  server_now: string;
  active_session?: ViewPassSession | null;
  plans: ViewPassPlan[];
  private_dashboard_url: string;
  next_action: string;
}

export interface X402ResourceInfo {
  url: string;
  description?: string;
  mimeType?: string;
  serviceName?: string;
  tags?: string[];
  iconUrl?: string;
}

export interface X402PaymentRequirement {
  scheme: string;
  network: string;
  asset: string;
  amount: string;
  payTo: string;
  maxTimeoutSeconds: number;
  extra: Record<string, unknown>;
}

export interface X402PaymentRequired {
  x402Version: number;
  error?: string;
  resource: X402ResourceInfo;
  accepts: X402PaymentRequirement[];
  extensions?: Record<string, unknown>;
}

export interface ViewPassQuote {
  schema_version: string;
  quote_id: string;
  plan: ViewPassPlan;
  price_micro_usd: number;
  price_usd: number;
  price_usdc: string;
  network: string;
  chain_id: string;
  recipient: string;
  expires_at: string;
  payment_required: X402PaymentRequired;
  signal_snapshot_hash: string;
  verification_hints: Record<string, unknown>;
  dev_mock_signature?: string | null;
}

export interface ViewPassActivateRequest {
  quote_id: string;
  wallet_address: string;
  payment_signature: string;
  payment_id?: string;
  chain_id?: string;
  idempotency_key?: string;
}

export interface ViewPassActivateResponse {
  status: string;
  message: string;
  session: ViewPassSession;
  private_dashboard_url: string;
}

function authHeaders(): HeadersInit {
  const token = useAuthStore.getState().token || localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function viewPassRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`View pass HTTP ${response.status}${detail ? `: ${detail.slice(0, 180)}` : ''}`);
  }
  return response.json() as Promise<T>;
}

export const viewPassApi = {
  plans: () => viewPassRequest<ViewPassPlan[]>('/v1/viewpass/plans'),
  status: () => viewPassRequest<ViewPassStatus>('/v1/viewpass/status'),
  quote: (planId: string) =>
    viewPassRequest<ViewPassQuote>('/v1/viewpass/x402/quote', {
      method: 'POST',
      body: JSON.stringify({ plan_id: planId }),
    }),
  activate: (payload: ViewPassActivateRequest) =>
    viewPassRequest<ViewPassActivateResponse>('/v1/viewpass/x402/activate', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

export const viewPassKeys = {
  plans: ['viewPass', 'plans'] as const,
  status: ['viewPass', 'status'] as const,
};

export function useViewPassPlans() {
  return useQuery({
    queryKey: viewPassKeys.plans,
    queryFn: viewPassApi.plans,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useViewPassStatus(enabled = true) {
  const accountId = useAuthStore((state) => state.user?.accountId);
  return useQuery({
    queryKey: [...viewPassKeys.status, accountId ?? 'anonymous'],
    queryFn: viewPassApi.status,
    enabled,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const remaining = query.state.data?.active_session?.remaining_seconds ?? 0;
      return remaining > 0 ? 30_000 : false;
    },
    retry: 1,
  });
}

export function useCreateViewPassQuote() {
  return useMutation({
    mutationFn: viewPassApi.quote,
  });
}

export function useActivateViewPass() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: viewPassApi.activate,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: viewPassKeys.status });
    },
  });
}
